import React, { useMemo } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { MarketingLayout } from "@/components/MarketingLayout";
import { SCHEMES, SCHEME_LIST, COMMON_SECTIONS } from "@/lib/schemes";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, CheckCircle2, FileCheck, ArrowLeft } from "lucide-react";

export default function SchemeInfo() {
  const { code } = useParams();
  const navigate = useNavigate();
  const scheme = SCHEMES[code];

  const provideFields = useMemo(
    () => (scheme ? [...COMMON_SECTIONS.flatMap((s) => s.fields), ...scheme.section.fields] : []),
    [scheme]
  );

  if (!scheme) return <Navigate to="/schemes" replace />;

  return (
    <MarketingLayout>
      <section className="hero-radial grain relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <div className="text-sm text-slate-500 mb-4"><Link to="/" className="hover:text-emerald-700">Home</Link> / <Link to="/schemes" className="hover:text-emerald-700">Schemes</Link> / {scheme.code}</div>
          <div className="flex items-start gap-5">
            <div className="h-16 w-16 rounded-2xl grid place-items-center shrink-0" style={{ backgroundColor: scheme.tint }}>
              <scheme.icon className="h-8 w-8" style={{ color: scheme.color }} />
            </div>
            <div>
              <h1 className="font-head font-extrabold text-2xl sm:text-3xl lg:text-4xl text-emerald-950 tracking-tight">{scheme.name}</h1>
              <p className="mt-3 text-lg text-slate-600 max-w-2xl">{scheme.short}</p>
            </div>
          </div>
          <Button className="mt-7 rounded-full bg-brand hover:bg-emerald-800 text-white h-12 px-7" onClick={() => navigate(`/apply?scheme=${scheme.code}`)} data-testid="scheme-apply-button">
            Apply for {scheme.code} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div>
            <h2 className="font-head font-bold text-2xl text-emerald-950 mb-5">Information you'll provide</h2>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {provideFields.map((f) => (
                <div key={f.key} className="flex gap-3 bg-white border border-emerald-100 rounded-2xl px-4 py-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-600">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-head font-bold text-2xl text-emerald-950 mb-5">Documents to keep ready</h2>
            <Card className="rounded-2xl p-6 border-emerald-100 bg-[#f1f6f2]">
              <div className="grid sm:grid-cols-2 gap-3">
                {scheme.documents.map((d) => (
                  <div key={d.key} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <FileCheck className="h-4 w-4 text-emerald-600" /> {d.label}
                    {d.required ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">required</span> : <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-500">if applicable</span>}
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div className="rounded-3xl bg-emerald-900 p-10 text-center grain relative overflow-hidden">
            <h3 className="font-head font-extrabold text-white text-2xl">Ready to apply for {scheme.code}?</h3>
            <p className="mt-3 text-emerald-100/80">Log in and we'll guide you through every step with a document checklist.</p>
            <Button className="mt-6 rounded-full bg-white text-emerald-900 hover:bg-emerald-50 h-12 px-8 font-semibold" onClick={() => navigate(`/apply?scheme=${scheme.code}`)}>Apply Now <ArrowRight className="ml-2 h-5 w-5" /></Button>
          </div>

          <div>
            <h3 className="font-head font-semibold text-emerald-950 mb-4">Other schemes</h3>
            <div className="flex flex-wrap gap-2">
              {SCHEME_LIST.filter((s) => s.code !== scheme.code).map((s) => (
                <Link key={s.code} to={`/scheme/${s.code}`} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 px-4 py-2 text-sm text-emerald-800 hover:bg-emerald-50">
                  <s.icon className="h-4 w-4" /> {s.code}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
